import { useQuery } from '@apollo/client/react';
import {
  BodyText,
  Button,
  Card,
  Flex,
  Heading,
  Link,
} from '@utilitywarehouse/hearth-react';
import {
  CopySmallIcon,
  ShareSmallIcon,
  TickSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import SpotSavingsLight from '@utilitywarehouse/hearth-svg-assets/lib/spot-savings-light.svg';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsInView } from '@hooks/useIsInView';
import {
  useTrackSignupReferAFriendBannerCopyUrlClicked,
  useTrackSignupReferAFriendBannerDisplayedOnce,
  useTrackSignupReferAFriendBannerShareUrlClicked,
} from '@tracking/trackingEvents';
import { getReferralCodeQuery } from '@utils/queries/attribution';

import './styles.scss';

interface ReferAFriendBannerProps {
  accountId: string;
}

const SHARE_TEXT = `I just switched to Utility Warehouse and I'm loving the savings. Use my link and we'll BOTH get £50 off our next bill - it's a win-win!`;

const ReferAFriendBanner = ({ accountId }: ReferAFriendBannerProps) => {
  const referAFriendBannerRef = useRef<HTMLElement>(null);
  const isInView = useIsInView(referAFriendBannerRef);
  const [copied, setCopied] = useState(false);
  const referralLinkInput = useRef<HTMLInputElement>(null);
  const hasNavigatorShare = !!navigator.share;
  const trackSignupReferAFriendBannerDisplayedOnce =
    useTrackSignupReferAFriendBannerDisplayedOnce();
  const trackSignupReferAFriendBannerCopyUrlClicked =
    useTrackSignupReferAFriendBannerCopyUrlClicked();
  const trackSignupReferAFriendBannerShareUrlClicked =
    useTrackSignupReferAFriendBannerShareUrlClicked();
  const { data, loading, error } = useQuery(getReferralCodeQuery, {
    variables: {
      accountId,
    },
  });

  const referralLink = `${window.location.origin}/ref/customer/${data?.referralCode}`;
  const hasClipboard = navigator.clipboard;

  const handleCopyReferralLink = useCallback(async () => {
    if (hasClipboard) {
      await navigator.clipboard.writeText(referralLink);
    } else {
      // fallback if clipboard is unavailable
      referralLinkInput?.current?.select();
      document.execCommand('copy');
    }
  }, [hasClipboard, referralLink]);

  const handleCopy = async () => {
    try {
      await handleCopyReferralLink();
      trackSignupReferAFriendBannerCopyUrlClicked();
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch {
      // silently fail
    }
  };

  const shareYourLink = async () => {
    if (navigator.share) {
      try {
        trackSignupReferAFriendBannerShareUrlClicked();
        await navigator.share({
          text: `${SHARE_TEXT} ${referralLink}`,
        });
      } catch {
        // silently fail
      }
    }
  };

  useEffect(() => {
    if (isInView) {
      trackSignupReferAFriendBannerDisplayedOnce();
    }
  }, [isInView, trackSignupReferAFriendBannerDisplayedOnce]);

  if (loading || error || !data?.referralCode) {
    return null;
  }

  return (
    <Flex
      direction="column"
      spacing="xl"
      maxWidth="800px"
      ref={referAFriendBannerRef}
    >
      <Card
        variant="subtle"
        spacing="lg"
        direction={{ tablet: 'row', mobile: 'column-reverse' }}
        alignItems="center"
        className="refer-a-friend-banner"
      >
        <Flex
          className="refer-a-friend-banner__content"
          direction="column"
          spacing="lg"
          justifyContent="between"
          alignItems={{ tablet: 'start', mobile: 'center' }}
          textAlign={{ tablet: 'left', mobile: 'center' }}
        >
          <Flex direction="column" spacing="md">
            <Heading as="h3">Get £50 off your bill</Heading>
            <BodyText marginBottom={{ mobile: '200', tablet: '100' }}>
              For each homeowner friend that switches two services to us, we'll
              give you £50 off your bill. Your friend will also get £50 - it's a
              win-win! See{' '}
              <Link
                href="https://uw.co.uk/legal/terms-conditions/refer-a-friend"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; conditions
              </Link>{' '}
              for details.
            </BodyText>

            <Flex
              direction={{ mobile: 'column', desktop: 'row' }}
              spacing="md"
              marginRight={{ desktop: '300' }}
            >
              <Flex
                direction="column"
                spacing="md"
                className="raf__hide-on-desktop"
              >
                {hasNavigatorShare && (
                  <Button onClick={shareYourLink}>
                    <ShareSmallIcon />
                    Share your link
                  </Button>
                )}

                <Button
                  className="raf__button-copy"
                  variant="outline"
                  size="md"
                  onClick={handleCopy}
                >
                  {copied ? <TickSmallIcon /> : <CopySmallIcon />}
                  {copied ? (
                    'Link copied!'
                  ) : (
                    <span className="raf__button-copy-text">
                      {referralLink}
                    </span>
                  )}
                </Button>
              </Flex>

              <Flex
                direction="row"
                spacing="md"
                className="raf__hide-on-mobile"
              >
                <input
                  type="text"
                  className={`raf__link ${copied ? 'raf__link--copied' : ''}`}
                  value={referralLink}
                  title={referralLink}
                  aria-label="Referral link"
                  readOnly
                />

                <Button
                  size="md"
                  onClick={handleCopy}
                  className="raf__button-copy-desktop"
                >
                  {copied ? <TickSmallIcon /> : <CopySmallIcon />}
                  {copied ? 'Link copied!' : 'Copy your link'}
                </Button>
              </Flex>

              <input
                ref={referralLinkInput}
                type="text"
                value={referralLink}
                aria-label="Referral link"
                readOnly
                title={referralLink}
                hidden
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          minWidth="200px"
          direction="column"
          spacing="lg"
          alignItems="center"
          justifyContent="center"
        >
          <img
            className="thankYou__spot-savings"
            src={SpotSavingsLight}
            alt="Spot savings"
          />
        </Flex>
      </Card>
    </Flex>
  );
};

export { ReferAFriendBanner };
